import React, { DOMAttributes, ForwardedRef, ReactElement } from 'react';
import clsx from 'clsx';
import styles from '@danske/sapphire-css/components/table/table.module.css';

import { TableContainerContext } from './TableContainerContext';
import { StickyPosition } from '..';
import {
  SapphireStyleProps,
  useSapphireStyleProps,
  useThemeCheck,
} from '../../utils';
import { GlobalDomAttributes } from '../../utils/types';
import { filterDOMProps } from '@react-aria/utils';
import { DOMProps } from '@react-types/shared';

export type TableContainerProps = GlobalDomAttributes &
  SapphireStyleProps &
  Omit<
    DOMAttributes<HTMLDivElement>,
    'className' | 'style' | 'dangerouslySetInnerHTML'
  > & {
    /**
     * Element rendered after all the elements of the table.
     * Normally this is where you would render a <Pagination> component.
     *
     * This should not be confused with a table footer, which is a part of the
     * table and renders a `tfoot` element.
     */
    footer?: ReactElement;

    /**
     * If the table header should be sticky relative to the first ancestor with
     * an `overflow`.
     * `auto` means it is is `true` if the table has a custom `height` or
     * `maxHeight` and `false` otherwise.
     *
     * @default 'auto'
     */
    stickyHeader?: StickyPosition;

    /**
     * If the table's footer should be sticky relative to the first ancestor
     * with an `overflow`.
     * `auto` means it is is `true` if the table has a custom `height` or
     * `maxHeight` and `false` otherwise.
     *
     * @default 'auto'
     */
    stickyFooter?: StickyPosition;

    isInteractive?: boolean;

    /**
     * Removes the divider after the last table row.
     * Useful when rendered in a container which already has borders.
     */
    hideLastDivider?: boolean;
    children?: React.ReactNode;
  };

/**
 * A wrapper around the {@Link Table} to provide sticky header behavior.
 * Accepts footer prop to render elements like pagination after the table.
 */

export const TableContainer = React.forwardRef(function TableContainer(
  {
    children,
    footer,
    stickyHeader = 'auto',
    stickyFooter = 'auto',
    hideLastDivider = false,
    isInteractive,
    ...props
  }: TableContainerProps,
  forwardedRef: ForwardedRef<HTMLDivElement>
) {
  useThemeCheck();
  const { styleProps, filteredProps } = useSapphireStyleProps(props);

  const hasCustomHeight =
    'height' in styleProps.style || 'maxHeight' in styleProps.style;
  const hasCustomWidth =
    'width' in styleProps.style || 'maxWidth' in styleProps.style;

  const shouldHeaderStick =
    stickyHeader === 'auto' ? hasCustomHeight || hasCustomWidth : stickyHeader;

  const shouldFooterStick =
    stickyFooter === 'auto' ? hasCustomHeight || hasCustomWidth : stickyFooter;

  const isFooterStickingToOuterContainer =
    stickyFooter && !hasCustomHeight && !hasCustomWidth;

  return (
    <TableContainerContext.Provider
      value={{ shouldHeaderStick, isInteractive }}
    >
      <div
        {...filterDOMProps(filteredProps as DOMProps, { global: true })}
        ref={forwardedRef}
        className={clsx(
          styles['sapphire-table'],
          {
            [styles['sapphire-table--without-last-divider']]: hideLastDivider,
            [styles['sapphire-table--overflow']]:
              hasCustomHeight || hasCustomWidth,
            [styles['sapphire-table--interactive']]: isInteractive,
          },
          styleProps.className
        )}
        style={styleProps.style}
      >
        {!shouldHeaderStick && isFooterStickingToOuterContainer ? (
          <div style={{ overflowX: 'auto' }}>{children}</div>
        ) : (
          children
        )}
        {!!footer && (
          <div
            className={clsx(styles['sapphire-table__footer'], {
              [styles['sapphire-table__footer--sticky']]: shouldFooterStick,
            })}
          >
            {footer}
          </div>
        )}
      </div>
    </TableContainerContext.Provider>
  );
});
